import React, { useState, useEffect, useRef } from 'react';
import { useGroup } from '../contexts/GroupContext';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import BottomNav from '../components/BottomNav';
import { formatMatchLocal, isMatchLocked } from '../utils/formatMatchDate';
import { ADMOB_INTERSTITIAL_ID, showAdsterraPopunder } from '../App';

// ── SOUND UTILITY ─────────────────────────────────────────
const SoundManager = {
  muted: localStorage.getItem('pitazo_muted') === 'true',

  play(type) {
    if (this.muted) return;
    try {
      let src = '';
      let volume = 0.7;
      let maxDuration = null;
      if (type === 'save') { src = '/sounds/gol.mp3'; volume = 0.4; maxDuration = 1500; }
      else if (type === 'goal') { src = '/sounds/gol.mp3'; volume = 0.8; }
      else if (type === 'trophy') { src = '/sounds/gol.mp3'; volume = 0.5; maxDuration = 2000; }
      if (!src) return;
      const audio = new Audio(src);
      audio.volume = volume;
      audio.play().catch(() => {});
      if (maxDuration) setTimeout(() => { audio.pause(); audio.currentTime = 0; }, maxDuration);
    } catch(e) {}
  },

  toggle() {
    this.muted = !this.muted;
    localStorage.setItem('pitazo_muted', this.muted);
    return this.muted;
  }
};

// ── ESTILOS MEJORADOS ─────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600;700&family=Inter:wght@300;400;500;600&display=swap');
  
  * { box-sizing: border-box; margin: 0; padding: 0; }
  
  body { 
    background: #080810; 
    color: #F5F5F0; 
    font-family: 'Inter', sans-serif; 
  }
  
  input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; }
  input[type=number] { -moz-appearance: textfield; }
  
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes fadeIn { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
  @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.7; } }
  @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
  
  .card-hover {
    transition: all 0.3s ease;
  }
  .card-hover:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(240, 180, 41, 0.1);
    border-color: rgba(240, 180, 41, 0.3);
  }
  
  .btn-gold {
    background: linear-gradient(135deg, #F0B429 0%, #C9820A 100%);
    transition: all 0.3s ease;
  }
  .btn-gold:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 15px rgba(240, 180, 41, 0.4);
  }
  
  .glass-effect {
    background: rgba(20, 20, 31, 0.8);
    backdrop-filter: blur(10px);
  }
  
  .gold-glow {
    box-shadow: 0 0 20px rgba(240, 180, 41, 0.15);
  }

`;

const C = {
  gold: '#F0B429', 
  goldDark: '#C9820A',
  green: '#06B86A', 
  red: '#E63946',
  bg: '#080810', 
  card: '#14141f', 
  cardHover: '#1a1a2a',
  border: 'rgba(255,255,255,0.08)',
  borderGold: 'rgba(240,180,41,0.25)'
};

const FLAG_MAP = {
  MEX:'mx', RSA:'za', KOR:'kr', CAN:'ca', USA:'us', PAR:'py',
  QAT:'qa', SUI:'ch', BRA:'br', MAR:'ma', HAI:'ht', SCO:'gb-sct',
  AUS:'au', GER:'de', CUW:'cw', NED:'nl', JPN:'jp', CIV:'ci',
  ECU:'ec', TUN:'tn', ESP:'es', CPV:'cv', BEL:'be', EGY:'eg',
  KSA:'sa', URU:'uy', IRN:'ir', NZL:'nz', FRA:'fr', SEN:'sn',
  NOR:'no', ARG:'ar', ALG:'dz', AUT:'at', JOR:'jo', POR:'pt',
  ENG:'gb-eng', CRO:'hr', GHA:'gh', PAN:'pa', COL:'co', UZB:'uz',
  CZE:'cz', BIH:'ba', TUR:'tr', SWE:'se', IRQ:'iq', CGO:'cg',
  TBD: null,
};

function FlagImg({ code, size = 40 }) {
  const iso = FLAG_MAP[code];
  if (!iso) return <span style={{ fontSize: size * 0.7, display: 'block', textAlign: 'center' }}>❓</span>;
  return (
    <img
      src={`https://flagcdn.com/w80/${iso}.png`}
      alt=""
      width={size}
      height={Math.round(size * 0.67)}
      style={{ borderRadius: 6, display: 'block', margin: '0 auto', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', objectFit: 'cover' }}
    />
  );
}

const PHASE_ORDER = ['Grupos','16avos','Octavos','Cuartos','Semifinal','Semifinal Final','Tercer puesto','Final'];
const getPhaseLabels = (t) => ({
  'Grupos': t('phase_grupos'),
  '16avos': t('phase_16avos'),
  'Octavos': t('phase_octavos'),
  'Cuartos': t('phase_cuartos'),
  'Semifinal': t('phase_semifinal'),
  'Semifinal Final': t('phase_semif'),
  'Tercer puesto': t('phase_tercero'),
  'Final': t('phase_final'),
});

// isLocked y formatDate reemplazados por utils/formatMatchDate.js

// ── AUTO EXPORT PDF ───────────────────────────────────────
async function autoExportPDF(fixture, group, members, predictions, allFixtures) {
  try {
    const matchPreds = predictions.filter(p => p.fixtureId === fixture.id);
    if (matchPreds.length === 0) return;

    const now = new Date().toLocaleString('es-CO');
    let rows = '';
    const sortedMembers = [...members].sort((a, b) => a.name.localeCompare(b.name));

    sortedMembers.forEach((m, i) => {
      const pred = matchPreds.find(p => p.userId === m.userId);
      const bg = i % 2 === 0 ? '#f9f9f9' : '#fff';
      rows += `<tr style="background:${bg}">
        <td style="padding:10px;border:1px solid #ddd;font-weight:600">${m.name}</td>
        <td style="padding:10px;border:1px solid #ddd;text-align:center;font-size:20px;font-weight:700;color:#C9820A">
          ${pred ? `${pred.homeScore} - ${pred.awayScore}` : `<span style="color:#ccc">Sin predicción</span>`}
        </td>
      </tr>`;
    });

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
      <title>Predicciones - ${fixture.homeTeamName} vs ${fixture.awayTeamName}</title>
      <style>body{font-family:Arial,sans-serif;max-width:500px;margin:20px auto} .header{background:#080810;color:#F0B429;padding:16px;border-radius:8px;margin-bottom:16px} table{border-collapse:collapse;width:100%}</style>
    </head><body>
      <div class="header">
        <div style="font-size:11px;color:#aaa;margin-bottom:4px">⚽ PITAZO MUNDIAL 2026 · ${group.name}</div>
        <h2 style="margin:0;font-size:20px">${fixture.homeTeamName} vs ${fixture.awayTeamName}</h2>
        <div style="margin-top:6px;font-size:12px;color:#aaa">${fixture.date} · ${fixture.time} · ${fixture.stadium}</div>
      </div>
      <p style="font-size:12px;color:#666;margin-bottom:12px">🔒 Predicciones cerradas · Generado: ${now}</p>
      <table>
        <thead><tr style="background:#080810;color:#F0B429">
          <th style="padding:10px;border:1px solid #ddd;text-align:left">{t('label_participant')}</th>
          <th style="padding:10px;border:1px solid #ddd">{t('label_prediction')}</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <p style="margin-top:16px;font-size:10px;color:#bbb;text-align:center">{t('lbl_auto_generated')}</p>
    </body></html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pred-${fixture.homeTeamName}-vs-${fixture.awayTeamName}.html`.replace(/\s+/g, '-');
    a.click();
    URL.revokeObjectURL(url);
  } catch(e) { console.error('Auto-export error:', e); }
}

// ── COMPONENTE PRINCIPAL ──────────────────────────────────
export default function ParticipantPage() {
  const { groupId: urlGroupId } = useParams();
  const { currentGroup, allGroups, fixtures, predictions, allPredictions, members, loading, savePrediction, calculateLeaderboard, switchGroup, activeGroupId } = useGroup();
  const { t } = useTranslation();
  const { userData, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (urlGroupId) localStorage.setItem('activeGroupId', urlGroupId);
  }, [urlGroupId]);

  // AdMob Banner — ahora manejado globalmente en App.js
  const [tab, setTab] = useState('matches');
  const [muted, setMuted] = useState(SoundManager.muted);
  const [phaseFilter, setPhaseFilter] = useState('Grupos');
  const [groupSubFilter, setGroupSubFilter] = useState('A');

  const exportedRef = useRef(new Set());
  useEffect(() => {
    if (!currentGroup || !fixtures || fixtures.length === 0) return;
    const uniqueFix = fixtures
      .filter((f, i, arr) => arr.findIndex(x => x.matchNumber === f.matchNumber) === i);
    const interval = setInterval(async () => {
      const now = new Date();
      for (const fixture of uniqueFix) {
        if (!fixture.lockTimestamp) continue;
        const lockDate = fixture.lockTimestamp.toDate ? fixture.lockTimestamp.toDate() : new Date(fixture.lockTimestamp);
        const diffMs = lockDate - now;
        if (diffMs >= -10000 && diffMs <= 50000 && !exportedRef.current.has(fixture.id)) {
          exportedRef.current.add(fixture.id);
          await autoExportPDF(fixture, currentGroup, members, predictions, uniqueFix);
        }
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [fixtures, currentGroup, members, predictions]);

  if (loading) return <LoadingScreen />;

  const leaderboard = calculateLeaderboard();
  const myPosition = leaderboard.findIndex(p => p.userId === userData?.uid);
  const myPoints = leaderboard[myPosition]?.points || 0;
  const myPredictions = predictions.filter(p => p.userId === userData?.uid);

  const uniqueFixtures = fixtures
    .filter((f, i, arr) => arr.findIndex(x => x.matchNumber === f.matchNumber) === i)
    .sort((a, b) => (a.matchNumber || 0) - (b.matchNumber || 0));

  const filteredFixtures = uniqueFixtures.filter(f => {
    if (f.phase !== phaseFilter) return false;
    if (phaseFilter === 'Grupos' && f.group !== groupSubFilter) return false;
    return true;
  });

  const tournamentGroups = [...new Set(uniqueFixtures.filter(f => f.phase === 'Grupos').map(f => f.group))]
    .filter(Boolean).sort();

  const phases = [...new Set(uniqueFixtures.map(f => f.phase))]
    .sort((a, b) => PHASE_ORDER.indexOf(a) - PHASE_ORDER.indexOf(b));

  return (
    <div style={{ minHeight: '100vh', background: `linear-gradient(180deg, #0a0a15 0%, ${C.bg} 100%)`, maxWidth: 480, margin: '0 auto', width: '100%' }}>
      <style>{CSS}</style>

      {/* HEADER MEJORADO */}
      <div style={{ 
        background: 'linear-gradient(180deg, rgba(240,180,41,0.05) 0%, transparent 100%)', 
        padding: '40px 16px 0', 
        position: 'sticky', 
        top: 0, 
        zIndex: 50,
        backdropFilter: 'blur(10px)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ 
              width: 44, 
              height: 44, 
              borderRadius: 12, 
              background: 'linear-gradient(135deg, #F0B429 0%, #C9820A 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 15px rgba(240, 180, 41, 0.3)'
            }}>
              <span style={{ fontSize: 24 }}>🏆</span>
            </div>
            <div>
              <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 20, fontWeight: 700, letterSpacing: 1 }}>
                PITAZO<span style={{ color: C.gold }}>MUNDIAL</span>
              </div>
              <div style={{ fontSize: 11, color: '#6B6B7B', marginTop: 2 }}>{currentGroup?.name || 'Mi Grupo'}</div>
            </div>
          </div>
          
          <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
            <button onClick={() => navigate('/mis-grupos')}
              style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, borderRadius: 8, padding: '5px 10px', fontFamily: "'Oswald',sans-serif", fontSize: 10, color: '#8B8B9B', cursor: 'pointer', letterSpacing: 1 }}>
              ← MIS GRUPOS
            </button>

            <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 14, letterSpacing: 1 }}>{userData?.name}</div>
            <div style={{ fontSize: 11, color: C.gold, fontWeight: 600 }}>#{myPosition + 1} · {myPoints} pts</div>
            
            <button onClick={() => setMuted(SoundManager.toggle())}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: '2px 4px', opacity: 0.7 }}
              title={muted ? t('btn_unmute') : t('btn_mute')}>
              {muted ? '🔇' : '🔊'}
            </button>
          </div>
        </div>

        {/* STATS CARDS MEJORADOS */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 16 }}>
          {[
            { label: t('stat_preds'), value: myPredictions.length, icon: '✓', color: C.green },
            { label: t('stat_points'), value: myPoints, icon: '🏆', color: C.gold },
            { label: t('stat_rank'), value: `#${myPosition + 1}`, icon: '📈', color: '#60A5FA' },
            { label: t('stat_group_size'), value: members.length, icon: '👥', color: '#A78BFA' },
          ].map((stat, i) => (
            <div key={stat.label} style={{
              background: 'rgba(20, 20, 31, 0.6)',
              border: `1px solid ${C.border}`,
              borderRadius: 12,
              padding: '10px 4px',
              textAlign: 'center',
              backdropFilter: 'blur(5px)'
            }}>
              <div style={{ fontSize: 16, marginBottom: 2 }}>{stat.icon}</div>
              <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 18, fontWeight: 700, color: stat.color }}>{stat.value}</div>
              <div style={{ fontSize: 9, color: '#6B6B7B', textTransform: 'uppercase', letterSpacing: 0.5 }}>{stat.label}</div>
            </div>
          ))}
        </div>

      </div>

      {/* SELECTOR DE GRUPOS */}
      {allGroups && allGroups.length > 1 && (
        <div style={{ padding: '12px 16px 0' }}>
          <div style={{ fontSize: 10, color: '#6B6B7B', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6, fontFamily: "'Inter',sans-serif" }}>
            Mis grupos
          </div>
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', scrollbarWidth: 'none', paddingBottom: 2 }}>
            {allGroups.map(g => {
              const isActive = activeGroupId === g.id || (!activeGroupId && currentGroup?.id === g.id);
              return (
                <button key={g.id} onClick={() => switchGroup(g.id)}
                  style={{
                    padding: '6px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
                    fontFamily: "'Oswald',sans-serif", fontSize: 11, letterSpacing: 1,
                    whiteSpace: 'nowrap', flexShrink: 0, transition: 'all 0.2s',
                    background: isActive ? C.gold : 'rgba(255,255,255,0.05)',
                    color: isActive ? '#080810' : '#6B6B7B',
                    boxShadow: isActive ? '0 2px 8px rgba(240,180,41,0.3)' : 'none'
                  }}>
                  {isActive ? '⚽ ' : ''}{g.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ADMOB BANNER PLACEHOLDER */}
      <div
        id="admob-banner-placeholder"
        style={{ height: 50, width: '100%', background: 'transparent', flexShrink: 0 }}
      />

      <div style={{ padding: '0 16px 0', animation: 'fadeIn 0.4s ease' }}>

        {/* BANNER */}
        <div style={{ padding: '12px 0 0' }}>
          <div onClick={() => navigate('/crear-grupo')}
            style={{ background: 'linear-gradient(90deg, rgba(240,180,41,0.15) 0%, rgba(240,180,41,0.05) 100%)', border: `1px solid ${C.borderGold}`, borderRadius: 16, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', marginBottom: 4 }}
            className="card-hover"
          >
            <div style={{ fontSize: 28 }}>🏆</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 13, letterSpacing: 1, color: C.gold, fontWeight: 600 }}>{t('banner_create_group')}</div>
              <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 11, color: '#6B6B7B', marginTop: 1 }}>{t('banner_create_group_sub')}</div>
            </div>
            <div style={{ color: C.gold, fontSize: 12 }}>→</div>
          </div>
        </div>

        {/* ── PARTIDOS ──────────────────────────────────── */}
        {tab === 'matches' && (
          <>
            {/* Filtro por fase */}
            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', padding: '16px 0', scrollbarWidth: 'none' }}>
              {phases.map(p => (
                <button key={p} onClick={() => setPhaseFilter(p)}
                  style={{ 
                    padding: '8px 16px', 
                    borderRadius: 20, 
                    border: 'none', 
                    cursor: 'pointer', 
                    fontFamily: "'Oswald',sans-serif", 
                    fontSize: 11, 
                    letterSpacing: 1, 
                    whiteSpace: 'nowrap',
                    background: phaseFilter === p ? `linear-gradient(135deg, ${C.gold} 0%, ${C.goldDark} 100%)` : C.card,
                    color: phaseFilter === p ? C.bg : '#6B6B7B',
                    fontWeight: phaseFilter === p ? 600 : 400,
                    boxShadow: phaseFilter === p ? '0 4px 15px rgba(240, 180, 41, 0.3)' : 'none',
                    transition: 'all 0.3s ease'
                  }}>
                  {getPhaseLabels(t)[p] || p.toUpperCase()}
                </button>
              ))}
            </div>

            {/* Sub-filtro de grupo del torneo (solo en fase Grupos) */}
            {phaseFilter === 'Grupos' && tournamentGroups.length > 0 && (
              <>
                <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 12, scrollbarWidth: 'none' }}>
                  {tournamentGroups.map(g => (
                    <button key={g} onClick={() => setGroupSubFilter(g)}
                      style={{
                        padding: '6px 14px', borderRadius: 10, border: 'none', cursor: 'pointer',
                        fontFamily: "'Oswald',sans-serif", fontSize: 12, letterSpacing: 1,
                        whiteSpace: 'nowrap', flexShrink: 0, transition: 'all 0.2s',
                        background: groupSubFilter === g
                          ? 'rgba(240,180,41,0.15)'
                          : 'rgba(255,255,255,0.04)',
                        color: groupSubFilter === g ? C.gold : '#555',
                        border: `1px solid ${groupSubFilter === g ? 'rgba(240,180,41,0.4)' : 'rgba(255,255,255,0.06)'}`,
                        fontWeight: groupSubFilter === g ? 700 : 400,
                      }}>
                      GRUPO {g}
                    </button>
                  ))}
                </div>

                {/* Tabla de posiciones del grupo */}
                <GroupStandingsTable
                  fixtures={uniqueFixtures}
                  tournamentGroup={groupSubFilter}
                />
              </>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {filteredFixtures.map((fixture, index) => (
                <MatchCard
                  key={fixture.id}
                  fixture={fixture}
                  prediction={predictions.find(p => p.userId === userData?.uid && p.fixtureId === fixture.id)}
                  allPredictions={allPredictions.filter(p => p.fixtureId === fixture.id)}
                  members={members}
                  onSave={savePrediction}
                  index={index}
                  groupPlan={currentGroup?.plan}
                  myUserId={userData?.uid}
                />
              ))}
            </div>
          </>
        )}

        {/* ── TABLA ─────────────────────────────────────── */}
        {tab === 'leaderboard' && (
          <div style={{ paddingTop: 16 }}>
            {/* Nota de desempate */}
            {leaderboard.length > 0 && (
              <div style={{ marginBottom: 14, padding: '10px 14px', background: 'rgba(240,180,41,0.05)', border: '1px solid rgba(240,180,41,0.15)', borderRadius: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 18, flexShrink: 0 }}>🏆</span>
                <div>
                  <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 11, color: C.gold, letterSpacing: 1, marginBottom: 2 }}>CRITERIO DE DESEMPATE</div>
                  <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 11, color: '#6B6B7B', lineHeight: 1.4 }}>
                    En caso de empate en puntos, gana quien tenga más resultados exactos.
                  </div>
                </div>
              </div>
            )}
            {leaderboard.length === 0 ? (
              <EmptyState icon="🏆" {...{msg: t('empty_no_points')}} />
            ) : leaderboard.map((player, i) => (
              <div key={player.userId}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 12, 
                  padding: '14px 16px', 
                  marginBottom: 10, 
                  borderRadius: 16, 
                  background: player.userId === userData?.uid ? 'rgba(240,180,41,0.08)' : C.card, 
                  border: `1px solid ${player.userId === userData?.uid ? C.borderGold : C.border}`,
                  boxShadow: player.userId === userData?.uid ? '0 4px 20px rgba(240, 180, 41, 0.1)' : 'none',
                  transition: 'all 0.3s ease'
                }}
                className="card-hover"
              >
                <div style={{ 
                  width: 36, 
                  height: 36, 
                  borderRadius: '50%', 
                  background: i < 3 ? 
                    ['linear-gradient(135deg,#FFD700,#FFA500)','linear-gradient(135deg,#C0C0C0,#A0A0A0)','linear-gradient(135deg,#CD7F32,#A0522D)'][i] : 
                    'rgba(255,255,255,0.05)', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  fontFamily: "'Oswald',sans-serif", 
                  fontSize: 14, 
                  fontWeight: 700, 
                  color: i < 3 ? C.bg : '#6B6B7B',
                  boxShadow: i < 3 ? '0 4px 10px rgba(0,0,0,0.3)' : 'none'
                }}>
                  {i + 1}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 15, letterSpacing: 0.5, fontWeight: 500 }}>
                    {player.name} {player.userId === userData?.uid && <span style={{ fontSize: 10, color: C.gold, background: 'rgba(240,180,41,0.1)', padding: '2px 8px', borderRadius: 10, marginLeft: 6 }}>TÚ</span>}
                  </div>
                  <div style={{ fontSize: 11, color: '#6B6B7B', marginTop: 2 }}>
                    {player.exact} exactos · {player.correct} correctos
                  </div>
                </div>
                <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 24, color: C.gold, fontWeight: 700 }}>
                  {player.points}<span style={{ fontSize: 11, color: '#555', marginLeft: 2 }}>pts</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── MIS PREDICCIONES ──────────────────────────── */}
        {tab === 'mypreds' && (
          <div style={{ paddingTop: 16 }}>

            {/* BANNER DE CRITERIOS DE PUNTOS */}
            {(() => {
              const rules = currentGroup?.pointRules || {};
              const criteria = [
                { icon: '🎯', label: t('score_exact'),     pts: rules.exactScore        || 5 },
                { icon: '🏆', label: t('score_winner'),    pts: rules.winner             || 1 },
                { icon: '📊', label: t('score_goaldiff'),  pts: rules.goalDiff           || 2 },
                { icon: '⚽', label: t('score_exactgoals'),pts: rules.exactGoalsPerTeam  || 2 },
              ];
              return (
                <div style={{
                  background: 'rgba(20,20,31,0.8)',
                  border: '1px solid rgba(212,175,55,0.25)',
                  borderRadius: 14,
                  padding: '12px 14px',
                  marginBottom: 16,
                }}>
                  <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 11, color: '#6B6B7B', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>
                    🏅 {t('lbl_point_criteria') || 'Criterios de puntos'}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {criteria.map(c => (
                      <div key={c.label} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: '8px 12px',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 14 }}>{c.icon}</span>
                          <span style={{ fontFamily: "'Inter',sans-serif", fontSize: 11, color: '#A0A0B0' }}>{c.label}</span>
                        </div>
                        <span style={{ fontFamily: "'Oswald',sans-serif", fontSize: 15, color: '#D4AF37', fontWeight: 700 }}>+{c.pts}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {myPredictions.length === 0 ? (
              <EmptyState icon="⚽" msg={t('lbl_no_preds')} />
            ) : (
              <>
                <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 13, color: '#6B6B7B', marginBottom: 12 }}>
                  {t('lbl_preds_saved', {n: myPredictions.length})}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {myPredictions.map(pred => {
                    const fixture = fixtures.find(f => f.id === pred.fixtureId);
                    if (!fixture) return null;
                    const locked = isMatchLocked(fixture.lockTimestamp);
                    const otherPreds = locked ? predictions.filter(p => p.fixtureId === pred.fixtureId && p.userId !== userData?.uid) : [];
                    return <PredCard key={pred.id} pred={pred} fixture={fixture} locked={locked} otherPreds={otherPreds} members={members} />;
                  })}
                </div>
              </>
            )}
          </div>
        )}
        {/* ── ESPECIALES ──────────────────────────────── */}
        {tab === 'specials' && (
          <div style={{ padding: '16px' }}>
            <SpecialPredictionsTab group={currentGroup} userId={userData?.uid} fixtures={uniqueFixtures} />
          </div>
        )}
      </div>

      {/* BARRA DE NAVEGACIÓN INFERIOR — al final para que el spacer quede abajo */}
      {(() => {
        const participantTabs = [
          { id: 'matches',     label: t('tab_matches'),   icon: '⚽' },
          { id: 'leaderboard', label: t('tab_leaderboard'), icon: '🏆' },
          { id: 'mypreds',     label: t('tab_mypreds'),  icon: '📋' },
          ...(currentGroup?.plan && currentGroup.plan !== 'free' ? [{ id: 'specials', label: t('tab_specials'), icon: '⭐' }] : []),
        ];
        return <BottomNav tabs={participantTabs} active={tab} onChange={setTab} />;
      })()}
    </div>
  );
}

// ── TABLA DE POSICIONES POR GRUPO ─────────────────────────
function GroupStandingsTable({ fixtures, tournamentGroup }) {
  const groupFixtures = fixtures.filter(
    f => f.phase === 'Grupos' && f.group === tournamentGroup
  );

  // Construir mapa de equipos
  const teamsMap = {};
  groupFixtures.forEach(f => {
    if (!teamsMap[f.homeTeam]) teamsMap[f.homeTeam] = {
      code: f.homeTeam, name: f.homeTeamName,
      PJ: 0, PG: 0, PE: 0, PP: 0, GF: 0, GC: 0
    };
    if (!teamsMap[f.awayTeam]) teamsMap[f.awayTeam] = {
      code: f.awayTeam, name: f.awayTeamName,
      PJ: 0, PG: 0, PE: 0, PP: 0, GF: 0, GC: 0
    };
  });

  // Calcular stats con resultados disponibles
  groupFixtures.forEach(f => {
    if (f.homeScore == null || f.awayScore == null) return;
    const h = teamsMap[f.homeTeam];
    const a = teamsMap[f.awayTeam];
    if (!h || !a) return;
    h.PJ++; a.PJ++;
    h.GF += Number(f.homeScore); h.GC += Number(f.awayScore);
    a.GF += Number(f.awayScore); a.GC += Number(f.homeScore);
    if (f.homeScore > f.awayScore)       { h.PG++; a.PP++; }
    else if (f.homeScore === f.awayScore) { h.PE++; a.PE++; }
    else                                  { h.PP++; a.PG++; }
  });

  const teams = Object.values(teamsMap)
    .map(t => ({ ...t, GD: t.GF - t.GC, PTS: t.PG * 3 + t.PE }))
    .sort((a, b) => b.PTS - a.PTS || b.GD - a.GD || b.GF - a.GF);

  if (teams.length === 0) return null;

  const COLS = ['PJ','PG','PE','PP','GF','GC','GD'];

  // Grid: #(16px) + bandera(22px) + nombre(flex) + 7 cols(26px) + PTS(32px)
  const gridCols = '16px 22px minmax(0,1fr) repeat(7, 26px) 32px';

  return (
    <div style={{
      marginBottom: 14,
      background: C.card,
      border: `1px solid ${C.border}`,
      borderRadius: 14,
      overflow: 'hidden',
      width: '100%'
    }}>
      {/* Cabecera */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: gridCols,
        gap: 2,
        padding: '7px 10px 6px',
        background: 'rgba(240,180,41,0.07)',
        borderBottom: `1px solid ${C.border}`,
        alignItems: 'center'
      }}>
        <div style={{ gridColumn: '1 / 4', fontFamily: "'Oswald',sans-serif", fontSize: 11, color: C.gold, letterSpacing: 1, fontWeight: 700 }}>
          GRUPO {tournamentGroup}
        </div>
        {COLS.map(col => (
          <div key={col} style={{ textAlign: 'center', fontFamily: "'Oswald',sans-serif", fontSize: 10, color: '#555', letterSpacing: 0.5 }}>
            {col}
          </div>
        ))}
        <div style={{ textAlign: 'center', fontFamily: "'Oswald',sans-serif", fontSize: 10, color: C.gold, letterSpacing: 0.5, fontWeight: 700 }}>
          PTS
        </div>
      </div>

      {/* Filas */}
      {teams.map((team, i) => {
        const isClassified = i < 2;
        return (
          <div key={team.code} style={{
            display: 'grid',
            gridTemplateColumns: gridCols,
            gap: 2,
            padding: '8px 10px',
            borderBottom: i < teams.length - 1 ? `1px solid rgba(255,255,255,0.04)` : 'none',
            background: isClassified ? 'rgba(6,184,106,0.03)' : 'transparent',
            alignItems: 'center'
          }}>
            {/* # */}
            <div style={{
              fontFamily: "'Oswald',sans-serif", fontSize: 11,
              color: isClassified ? C.green : '#444', fontWeight: 700, textAlign: 'center'
            }}>
              {i + 1}
            </div>

            {/* Bandera */}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <FlagImg code={team.code} size={18} />
            </div>

            {/* Nombre */}
            <div style={{
              fontFamily: "'Oswald',sans-serif", fontSize: 11, color: '#ccc',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              letterSpacing: 0.3, paddingLeft: 4
            }}>
              {team.name}
            </div>

            {/* PJ PG PE PP GF GC GD */}
            {COLS.map(col => (
              <div key={col} style={{
                textAlign: 'center',
                fontFamily: "'Oswald',sans-serif",
                fontSize: 12,
                color: col === 'GD'
                  ? (team[col] > 0 ? C.green : team[col] < 0 ? C.red : '#555')
                  : '#888'
              }}>
                {col === 'GD' && team[col] > 0 ? `+${team[col]}` : team[col]}
              </div>
            ))}

            {/* PTS */}
            <div style={{
              textAlign: 'center',
              fontFamily: "'Oswald',sans-serif",
              fontSize: 14,
              fontWeight: 700,
              color: team.PTS > 0 ? C.gold : '#444'
            }}>
              {team.PTS}
            </div>
          </div>
        );
      })}

      {/* Leyenda */}
      <div style={{
        padding: '5px 10px',
        borderTop: `1px solid rgba(255,255,255,0.04)`,
        display: 'flex', alignItems: 'center', gap: 5
      }}>
        <div style={{ width: 7, height: 7, borderRadius: '50%', background: C.green, flexShrink: 0 }} />
        <span style={{ fontSize: 9, color: '#444', fontFamily: "'Inter',sans-serif", letterSpacing: 0.5 }}>
          Clasifican
        </span>
      </div>
    </div>
  );
}

// ── SPECIAL PREDICTIONS TAB ──────────────────────────────
const MUNDIAL_START = new Date('2026-06-11T17:00:00Z');
const TOURNAMENT_GROUPS = ['A','B','C','D','E','F','G','H','I','J','K','L'];

// Lista de equipos igual a la del SuperAdmin (mismo código → comparación exacta)
const ALL_TEAMS_SPECIAL = [
  { code: 'ARG', name: 'Argentina' }, { code: 'AUS', name: 'Australia' },
  { code: 'AUT', name: 'Austria' }, { code: 'BEL', name: 'Bélgica' },
  { code: 'BRA', name: 'Brasil' }, { code: 'CAN', name: 'Canadá' },
  { code: 'COL', name: 'Colombia' }, { code: 'CIV', name: 'Costa de Marfil' },
  { code: 'CPV', name: 'Cabo Verde' }, { code: 'CRO', name: 'Croacia' },
  { code: 'CUW', name: 'Curaçao' }, { code: 'ECU', name: 'Ecuador' },
  { code: 'EGY', name: 'Egipto' }, { code: 'ENG', name: 'Inglaterra' },
  { code: 'ESP', name: 'España' }, { code: 'FRA', name: 'Francia' },
  { code: 'GER', name: 'Alemania' }, { code: 'GHA', name: 'Ghana' },
  { code: 'HAI', name: 'Haití' }, { code: 'IRN', name: 'Irán' },
  { code: 'JOR', name: 'Jordania' }, { code: 'JPN', name: 'Japón' },
  { code: 'KOR', name: 'Corea del Sur' }, { code: 'KSA', name: 'Arabia Saudita' },
  { code: 'MAR', name: 'Marruecos' }, { code: 'MEX', name: 'México' },
  { code: 'NED', name: 'Países Bajos' }, { code: 'NOR', name: 'Noruega' },
  { code: 'NZL', name: 'Nueva Zelanda' }, { code: 'PAN', name: 'Panamá' },
  { code: 'PAR', name: 'Paraguay' }, { code: 'POR', name: 'Portugal' },
  { code: 'QAT', name: 'Qatar' }, { code: 'RSA', name: 'Sudáfrica' },
  { code: 'SCO', name: 'Escocia' }, { code: 'SEN', name: 'Senegal' },
  { code: 'SUI', name: 'Suiza' }, { code: 'TUN', name: 'Túnez' },
  { code: 'URU', name: 'Uruguay' }, { code: 'USA', name: 'Estados Unidos' },
  { code: 'UZB', name: 'Uzbekistán' }, { code: 'ALG', name: 'Argelia' },
  { code: 'CZE', name: 'República Checa' }, { code: 'BIH', name: 'Bosnia-Herzegovina' },
  { code: 'TUR', name: 'Turquía' }, { code: 'SWE', name: 'Suecia' },
  { code: 'IRQ', name: 'Iraq' }, { code: 'CGO', name: 'Congo' },
].sort((a, b) => a.name.localeCompare(b.name));

function SpecialPredictionsTab({ group, userId, fixtures = [] }) {
  const { t } = useTranslation();
  const [preds, setPreds] = useState({});
  const [savingPodio, setSavingPodio] = useState(false);
  const [savedPodio, setSavedPodio] = useState(false);
  const [savingGroups, setSavingGroups] = useState(false);
  const [savedGroups, setSavedGroups] = useState(false);
  const [loading, setLoading] = useState(true);
  const isLocked = new Date() >= MUNDIAL_START;
  const timeLeft = Math.max(0, MUNDIAL_START - new Date());
  const daysLeft = Math.floor(timeLeft / 86400000);
  const hoursLeft = Math.floor((timeLeft % 86400000) / 3600000);

  // Puntos configurados por el admin
  const rules = group?.pointRules || {};
  const podioPoints = { champion: rules.champion||15, runnerUp: rules.runnerUp||10, thirdPlace: rules.thirdPlace||8, fourthPlace: rules.fourthPlace||5 };
  const topScorerPoints = rules.topScorer || 10;
  const nextRoundPoints = rules.nextRound || 3; // por clasificado correcto

  const [specialBreakdown, setSpecialBreakdown] = useState([]);
  const [specialPointsTotal, setSpecialPointsTotal] = useState(0);

  useEffect(() => {
    if (!group?.id || !userId) return;
    async function load() {
      setLoading(true);
      try {
        const snap = await getDoc(doc(db, 'specialPredictions', `${group.id}_${userId}`));
        if (snap.exists()) {
          setPreds(snap.data().predictions || {});
          setSpecialBreakdown(snap.data().specialBreakdown || []);
          setSpecialPointsTotal(snap.data().specialPoints || 0);
        }
      } catch(e) { console.error(e); }
      setLoading(false);
    }
    load();
  }, [group?.id, userId]);

  async function savePodio() {
    setSavingPodio(true);
    try {
      await setDoc(doc(db, 'specialPredictions', `${group.id}_${userId}`), {
        groupId: group.id, userId, predictions: preds, updatedAt: serverTimestamp()
      }, { merge: true });
      setSavedPodio(true);
      setTimeout(() => setSavedPodio(false), 2500);
    } catch(e) { console.error(e); }
    setSavingPodio(false);
  }

  async function saveGroups() {
    setSavingGroups(true);
    try {
      await setDoc(doc(db, 'specialPredictions', `${group.id}_${userId}`), {
        groupId: group.id, userId, predictions: preds, updatedAt: serverTimestamp()
      }, { merge: true });
      setSavedGroups(true);
      setTimeout(() => setSavedGroups(false), 2500);
    } catch(e) { console.error(e); }
    setSavingGroups(false);
  }

  // Campo de selección de equipo (podio) — guarda el CODE igual que el superadmin
  function TeamSelectField({ label, icon, fieldKey, pts }) {
    const selected = ALL_TEAMS_SPECIAL.find(t => t.code === preds[fieldKey]);
    return (
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <label style={{ fontSize: 11, color: '#6B6B7B', letterSpacing: 1, textTransform: 'uppercase', fontFamily: "'Inter',sans-serif" }}>
            {icon} {label}
          </label>
          <span style={{ fontSize: 11, color: C.gold, fontFamily: "'Oswald',sans-serif", letterSpacing: 1 }}>{pts} pts</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {selected && (
            <img src={`https://flagcdn.com/w80/${FLAG_MAP[selected.code]}.png`} alt=""
              width={28} height={19}
              style={{ borderRadius: 4, objectFit: 'cover', flexShrink: 0 }} />
          )}
          <select
            value={preds[fieldKey] || ''}
            onChange={e => setPreds(p => ({ ...p, [fieldKey]: e.target.value }))}
            disabled={isLocked}
            style={{ flex: 1, padding: '11px 14px', background: isLocked ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.3)', border: `1px solid ${preds[fieldKey] ? C.borderGold : C.border}`, borderRadius: 10, color: isLocked ? '#555' : (preds[fieldKey] ? '#F5F5F0' : '#888'), fontSize: 13, fontFamily: "'Inter',sans-serif", appearance: 'none', WebkitAppearance: 'none', cursor: isLocked ? 'default' : 'pointer' }}
          >
            <option value="">{isLocked ? '🔒 Cerrado' : '— Seleccionar equipo —'}</option>
            {ALL_TEAMS_SPECIAL.map(t => (
              <option key={t.code} value={t.code}>{t.name}</option>
            ))}
          </select>
        </div>
      </div>
    );
  }

  // Campo de texto libre (goleador) — comparación normalizada en el servidor
  function Field({ label, icon, fieldKey, placeholder, pts }) {
    return (
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <label style={{ fontSize: 11, color: '#6B6B7B', letterSpacing: 1, textTransform: 'uppercase', fontFamily: "'Inter',sans-serif" }}>
            {icon} {label}
          </label>
          <span style={{ fontSize: 11, color: C.gold, fontFamily: "'Oswald',sans-serif", letterSpacing: 1 }}>{pts} pts</span>
        </div>
        <input
          value={preds[fieldKey] || ''}
          onChange={e => setPreds(p => ({ ...p, [fieldKey]: e.target.value }))}
          disabled={isLocked}
          placeholder={isLocked ? t('match_locked_placeholder') : placeholder}
          style={{ width: '100%', padding: '11px 14px', background: isLocked ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.3)', border: `1px solid ${C.border}`, borderRadius: 10, color: isLocked ? '#555' : '#F5F5F0', fontSize: 14, fontFamily: "'Inter',sans-serif" }}
        />
        <div style={{ fontSize: 10, color: '#555', fontFamily: "'Inter',sans-serif", marginTop: 4 }}>
          💡 Escribe el nombre y apellido completo (ej: Kylian Mbappé). No importan mayúsculas ni minúsculas.
        </div>
      </div>
    );
  }

  // Calcular cuántos clasificados tiene completos para mostrar progreso
  const totalGroupSlots = TOURNAMENT_GROUPS.length * 2;
  const filledGroupSlots = TOURNAMENT_GROUPS.reduce((acc, g) => acc + (preds[`group${g}_1`] ? 1 : 0) + (preds[`group${g}_2`] ? 1 : 0), 0);
  const maxGroupPoints = totalGroupSlots * nextRoundPoints;

  // Equipos por grupo — solo los del fixture de fase Grupos
  const teamsPerGroup = {};
  TOURNAMENT_GROUPS.forEach(g => { teamsPerGroup[g] = []; });
  fixtures.filter(f => f.phase === 'Grupos').forEach(f => {
    const g = f.group;
    if (!g || !teamsPerGroup[g]) return;
    if (f.homeTeamName && !teamsPerGroup[g].includes(f.homeTeamName)) teamsPerGroup[g].push(f.homeTeamName);
    if (f.awayTeamName && !teamsPerGroup[g].includes(f.awayTeamName)) teamsPerGroup[g].push(f.awayTeamName);
  });

  if (loading) return <div style={{ textAlign: 'center', padding: 40, color: '#6B6B7B' }}>{t('lbl_loading_specials')}</div>;

  return (
    <div>
      {/* Banner tiempo */}
      {isLocked ? (
        <div style={{ background: 'rgba(230,57,70,0.08)', border: '1px solid rgba(230,57,70,0.2)', borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: '#E63946', fontFamily: "'Inter',sans-serif" }}>
          {t('lbl_specials_closed')}
        </div>
      ) : (
        <div style={{ background: 'rgba(240,180,41,0.06)', border: '1px solid rgba(240,180,41,0.2)', borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: C.gold, fontFamily: "'Inter',sans-serif" }}>
          {t('lbl_specials_time', {d: daysLeft, h: hoursLeft})}
        </div>
      )}

      {/* BANNER DESGLOSE DE PUNTOS GANADOS */}
      {specialPointsTotal > 0 && (
        <div style={{
          background: 'rgba(6,184,106,0.07)',
          border: '1px solid rgba(6,184,106,0.25)',
          borderRadius: 14,
          padding: '12px 14px',
          marginBottom: 12,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 13, color: '#06B86A', letterSpacing: 1 }}>
              ✅ PUNTOS ESPECIALES GANADOS
            </div>
            <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 20, color: '#06B86A', fontWeight: 700 }}>
              +{specialPointsTotal} pts
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {specialBreakdown.map((item, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                background: 'rgba(6,184,106,0.06)', borderRadius: 8, padding: '7px 10px',
              }}>
                <span style={{ fontFamily: "'Inter',sans-serif", fontSize: 12, color: '#A0A0B0' }}>{item.label}</span>
                <span style={{ fontFamily: "'Oswald',sans-serif", fontSize: 14, color: '#06B86A', fontWeight: 600 }}>+{item.pts} pts</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* BANNER RESUMEN DE PUNTOS ESPECIALES */}
      <div style={{
        background: 'rgba(20,20,31,0.8)',
        border: '1px solid rgba(212,175,55,0.25)',
        borderRadius: 14,
        padding: '12px 14px',
        marginBottom: 16,
      }}>
        <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 11, color: '#6B6B7B', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>
          🏅 Puntos por ítem especial
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {[
            { icon: '🥇', label: t('lbl_champion_field'), pts: podioPoints.champion },
            { icon: '🥈', label: t('pl_runner'),          pts: podioPoints.runnerUp },
            { icon: '🥉', label: t('pl_third'),           pts: podioPoints.thirdPlace },
            { icon: '4️⃣', label: t('pl_fourth'),          pts: podioPoints.fourthPlace },
            { icon: '👟', label: t('lbl_scorer_field'),   pts: topScorerPoints },
            { icon: '➡️', label: 'Clasificado c/grupo',   pts: nextRoundPoints },
          ].map(c => (
            <div key={c.label} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: '8px 12px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 13 }}>{c.icon}</span>
                <span style={{ fontFamily: "'Inter',sans-serif", fontSize: 10, color: '#A0A0B0', lineHeight: 1.2 }}>{c.label}</span>
              </div>
              <span style={{ fontFamily: "'Oswald',sans-serif", fontSize: 15, color: '#D4AF37', fontWeight: 700, flexShrink: 0, marginLeft: 4 }}>+{c.pts}</span>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 8, padding: '6px 8px', background: 'rgba(240,180,41,0.04)', borderRadius: 8, fontSize: 10, color: '#666', fontFamily: "'Inter',sans-serif" }}>
          💡 Clasificados: máximo <strong style={{color: C.gold}}>{maxGroupPoints} pts</strong> si aciertas los {totalGroupSlots} clasificados
        </div>
      </div>

      {/* Podio + Goleador */}
      <div style={{ background: 'rgba(20,20,31,0.8)', border: `1px solid ${C.border}`, borderRadius: 14, padding: '16px', marginBottom: 16 }}>
        <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 13, letterSpacing: 1, color: C.gold, marginBottom: 14 }}>{`🏆 ${t('lbl_podio')}`}</div>
        <TeamSelectField label={t('lbl_champion_field')} icon="🥇" fieldKey="champion" pts={podioPoints.champion} />
        <TeamSelectField label={t('pl_runner')} icon="🥈" fieldKey="runnerUp" pts={podioPoints.runnerUp} />
        <TeamSelectField label={t('pl_third')} icon="🥉" fieldKey="thirdPlace" pts={podioPoints.thirdPlace} />
        <TeamSelectField label={t('pl_fourth')} icon="4️⃣" fieldKey="fourthPlace" pts={podioPoints.fourthPlace} />
        <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 16, paddingTop: 16 }}>
          <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 13, letterSpacing: 1, color: C.gold, marginBottom: 14 }}>{`👟 ${t('lbl_top_scorer_title')}`}</div>
          <Field {...{label: t('lbl_scorer_field'), icon: '⚽', fieldKey: 'topScorer', placeholder: t('placeholder_scorer')}} pts={topScorerPoints} />
        </div>
        {!isLocked && (
          <button onClick={savePodio} disabled={savingPodio}
            style={{ width: '100%', marginTop: 8, padding: '13px', background: savedPodio ? C.green : `linear-gradient(135deg, ${C.gold}, ${C.goldDark})`, border: 'none', borderRadius: 12, color: C.bg, fontFamily: "'Oswald',sans-serif", fontSize: 14, fontWeight: 700, letterSpacing: 1, cursor: 'pointer', transition: 'all 0.3s' }}>
            {savingPodio ? t('btn_saving') : savedPodio ? t('btn_saved') : t('btn_save_podium')}
          </button>
        )}
      </div>

      {/* Clasificados por grupo */}
      <div style={{ background: 'rgba(20,20,31,0.8)', border: `1px solid ${C.border}`, borderRadius: 14, padding: '16px', marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 13, letterSpacing: 1, color: C.gold }}>➡️ CLASIFICADOS POR GRUPO</div>
          <span style={{ fontSize: 11, color: C.gold, fontFamily: "'Oswald',sans-serif" }}>{nextRoundPoints} pts c/u</span>
        </div>
        {/* Info puntuación proporcional */}
        <div style={{ background: 'rgba(240,180,41,0.04)', border: '1px solid rgba(240,180,41,0.1)', borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 11, color: '#888', fontFamily: "'Inter',sans-serif", lineHeight: 1.5 }}>
          💡 <strong style={{color: C.gold}}>Puntuación proporcional:</strong> Ganas {nextRoundPoints} pts por cada clasificado que aciertes. 
          Si aciertas 12 de 24 → {Math.round(12 * nextRoundPoints)} pts. Si aciertas 24 de 24 → {maxGroupPoints} pts máx.
          <br/>Progreso actual: <strong style={{color: filledGroupSlots === totalGroupSlots ? C.green : C.gold}}>{filledGroupSlots}/{totalGroupSlots}</strong> completados.
        </div>
        {TOURNAMENT_GROUPS.map(g => {
          // Solo los equipos de este grupo extraídos de los fixtures
          const groupTeams = teamsPerGroup[g] || [];
          const selectStyle = key => ({
            padding: '9px 12px',
            background: isLocked ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.3)',
            border: `1px solid ${preds[key] ? 'rgba(240,180,41,0.3)' : C.border}`,
            borderRadius: 8,
            color: isLocked ? '#555' : (preds[key] ? '#F5F5F0' : '#888'),
            fontSize: 13,
            fontFamily: "'Inter',sans-serif",
            appearance: 'none',
            WebkitAppearance: 'none',
            width: '100%',
            cursor: isLocked ? 'default' : 'pointer',
          });
          return (
            <div key={g} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, color: '#6B6B7B', letterSpacing: 1, fontFamily: "'Oswald',sans-serif", marginBottom: 6 }}>GRUPO {g}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {[1, 2].map(pos => {
                  const key = `group${g}_${pos}`;
                  return groupTeams.length > 0 ? (
                    <select
                      key={pos}
                      value={preds[key] || ''}
                      onChange={e => setPreds(p => ({ ...p, [key]: e.target.value }))}
                      disabled={isLocked}
                      style={selectStyle(key)}
                    >
                      <option value="">{isLocked ? '🔒' : pos === 1 ? t('podium_1st') : t('podium_2nd')}</option>
                      {groupTeams.map(name => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      key={pos}
                      value={preds[key] || ''}
                      onChange={e => setPreds(p => ({ ...p, [key]: e.target.value }))}
                      disabled={isLocked}
                      placeholder={isLocked ? t('match_locked_short') : pos === 1 ? t('podium_1st') : t('podium_2nd')}
                      style={selectStyle(key)}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
        {!isLocked && (
          <button onClick={saveGroups} disabled={savingGroups}
            style={{ width: '100%', marginTop: 8, padding: '13px', background: savedGroups ? C.green : `linear-gradient(135deg, ${C.gold}, ${C.goldDark})`, border: 'none', borderRadius: 12, color: C.bg, fontFamily: "'Oswald',sans-serif", fontSize: 14, fontWeight: 700, letterSpacing: 1, cursor: 'pointer', transition: 'all 0.3s' }}>
            {savingGroups ? t('btn_saving') : savedGroups ? t('btn_saved') : t('btn_save_qualifiers')}
          </button>
        )}
      </div>
    </div>
  );
}

// ── PRED CARD MEJORADO ───────────────────────────────────
function PredCard({ pred, fixture, locked, otherPreds = [], members = [] }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  function handleOpen() {
    setOpen(o => {
      if (!o && totalPts > 0) SoundManager.play('goal');
      return !o;
    });
  }

  const ALL_CRITERIA = [
    { key: 'exactScore',  label: t('score_exact'),          desc: t('score_exact_desc') },
    { key: 'winner',      label: t('score_winner'),          desc: t('score_winner_desc') },
    { key: 'goalDiff',    label: t('score_goaldiff'),       desc: t('score_goaldiff_desc') },
    { key: 'exactGoals',  label: t('score_exactgoals'), desc: t('score_exactgoals_desc') },
  ];

  const breakdown = pred.breakdown || [];
  const totalPts = pred.points || 0;

  return (
    <div style={{ 
      marginBottom: 8, 
      borderRadius: 16, 
      background: pred.calculated ? (totalPts > 0 ? 'rgba(6, 184, 106, 0.05)' : C.card) : C.card, 
      border: `1px solid ${pred.calculated ? (totalPts > 0 ? 'rgba(6, 184, 106, 0.2)' : C.border) : C.border}`,
      overflow: 'hidden',
      transition: 'all 0.3s ease'
    }}
    className="card-hover"
    >
      <div onClick={() => pred.calculated && setOpen(o => !o)}
        style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 12, 
          padding: '14px 16px', 
          cursor: pred.calculated ? 'pointer' : 'default' 
        }}>
        <FlagImg code={fixture.homeTeam} size={32} />
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 14, letterSpacing: 0.5, fontWeight: 500 }}>
            {fixture.homeTeamName} vs {fixture.awayTeamName}
          </div>
          <div style={{ fontSize: 11, color: '#6B6B7B', marginTop: 2 }}>{formatDate(fixture)}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ 
            fontFamily: "'Oswald',sans-serif", 
            fontSize: 22, 
            color: locked && !pred.calculated ? '#6B6B7B' : C.gold, 
            letterSpacing: 2,
            fontWeight: 700
          }}>
            {pred.homeScore}-{pred.awayScore}
          </div>
          {pred.calculated && (
            <div style={{ 
              fontFamily: "'Oswald',sans-serif", 
              fontSize: 13, 
              color: totalPts > 0 ? C.green : '#555',
              fontWeight: 600
            }}>
              {totalPts > 0 ? `+${totalPts} pts` : '0 pts'}
            </div>
          )}
        </div>
        <FlagImg code={fixture.awayTeam} size={32} />
        {pred.calculated && (
          <div style={{ color: '#6B6B7B', fontSize: 12, transition: 'transform 0.3s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</div>
        )}
      </div>

      {pred.calculated && open && (
        <div style={{ borderTop: `1px solid ${C.border}`, padding: '14px 16px' }}>
          {fixture.homeScore !== null && fixture.homeScore !== undefined && (
            <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 12, color: '#6B6B7B', marginBottom: 14, textAlign: 'center' }}>
              Resultado: <span style={{ color: '#F5F5F0', fontFamily: "'Oswald',sans-serif", fontWeight: 600 }}>{fixture.homeScore} - {fixture.awayScore}</span>
              {'  ·  '}
              Tu predicción: <span style={{ color: C.gold, fontFamily: "'Oswald',sans-serif", fontWeight: 600 }}>{pred.homeScore} - {pred.awayScore}</span>
            </div>
          )}
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {ALL_CRITERIA.map(c => {
              const earned = breakdown.find(b => b.key === c.key);
              const pts = earned ? earned.pts : 0;
              const hit = pts > 0;
              return (
                <div key={c.key} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 10, 
                  padding: '10px 12px', 
                  borderRadius: 10, 
                  background: hit ? 'rgba(6, 184, 106, 0.08)' : 'rgba(255,255,255,0.02)',
                  border: `1px solid ${hit ? 'rgba(6, 184, 106, 0.15)' : 'rgba(255,255,255,0.05)'}`
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 12, color: hit ? C.green : '#555', fontWeight: hit ? 500 : 400 }}>{c.label}</div>
                    <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 10, color: '#444', marginTop: 1 }}>{c.desc}</div>
                  </div>
                  <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 16, color: hit ? C.green : '#333', minWidth: 40, textAlign: 'right', fontWeight: 600 }}>
                    {hit ? `+${pts}` : '0'}
                  </div>
                </div>
              );
            })}
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              padding: '10px 12px', 
              borderTop: `1px solid ${C.border}`, 
              marginTop: 4 
            }}>
              <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 13, letterSpacing: 1, color: '#6B6B7B', fontWeight: 600 }}>TOTAL</div>
              <div style={{ 
                fontFamily: "'Oswald',sans-serif", 
                fontSize: 20, 
                color: totalPts > 0 ? C.green : '#555',
                fontWeight: 700
              }}>
                {totalPts > 0 ? `+${totalPts}` : '0'} <span style={{ fontSize: 11 }}>pts</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {!pred.calculated && (
        <div style={{ 
          borderTop: `1px solid ${C.border}`, 
          padding: '10px 16px', 
          fontFamily: "'Inter',sans-serif", 
          fontSize: 11, 
          color: '#444',
          display: 'flex',
          alignItems: 'center',
          gap: 6
        }}>
          <span style={{ animation: 'pulse 2s infinite' }}>⏳</span> {t('lbl_pending_result')}
        </div>
      )}

      {/* PREDICCIONES DE OTROS — solo visibles cuando el partido ya cerró */}
      {locked && otherPreds && otherPreds.length > 0 && (
        <OtherPredictions otherPreds={otherPreds} members={members} fixture={fixture} />
      )}
    </div>
  );
}

// ── PREDICCIONES DE OTROS PARTICIPANTES ─────────────────
function OtherPredictions({ otherPreds, members, fixture }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (!otherPreds || otherPreds.length === 0) return null;

  return (
    <div style={{ borderTop: `1px solid rgba(255,255,255,0.06)` }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', background: 'none', border: 'none', cursor: 'pointer',
          padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: "'Inter',sans-serif", fontSize: 11, color: '#6B6B7B' }}>
          <span>👥</span>
          <span>Ver predicciones de otros ({otherPreds.length})</span>
        </div>
        <span style={{ color: '#6B6B7B', fontSize: 11, transition: 'transform 0.3s', display: 'inline-block', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
      </button>

      {open && (
        <div style={{ padding: '0 16px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {otherPreds.map(p => {
            const member = members.find(m => m.userId === p.userId || m.id === p.userId);
            const name = member?.name || 'Participante';
            const pts = p.points || 0;
            const isExact = p.breakdown?.some(b => b.key === 'exactScore');
            return (
              <div key={p.id || p.userId} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '8px 12px', borderRadius: 10,
                background: isExact ? 'rgba(240,180,41,0.06)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${isExact ? 'rgba(240,180,41,0.2)' : 'rgba(255,255,255,0.05)'}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: isExact ? 'rgba(240,180,41,0.15)' : 'rgba(255,255,255,0.06)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: "'Oswald',sans-serif", fontSize: 12, color: isExact ? '#F0B429' : '#6B6B7B', fontWeight: 700,
                  }}>
                    {name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 12, color: '#ccc' }}>{name}</div>
                    {p.calculated && pts > 0 && (
                      <div style={{ fontSize: 10, color: '#06B86A', fontFamily: "'Oswald',sans-serif" }}>+{pts} pts {isExact ? '🎯' : ''}</div>
                    )}
                  </div>
                </div>
                <div style={{
                  fontFamily: "'Oswald',sans-serif", fontSize: 18, fontWeight: 700,
                  color: isExact ? '#F0B429' : '#888', letterSpacing: 2,
                }}>
                  {p.homeScore}-{p.awayScore}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── MATCH CARD MEJORADO ───────────────────────────────────

// ── COMPONENTE TEMPORIZADOR ──────────────────────────────
function Countdown({ fixture }) {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = React.useState('');
  const [urgent, setUrgent] = React.useState(false);

  React.useEffect(() => {
    if (!fixture.lockTimestamp) return;
    let mounted = true;
    function update() {
      if (!mounted) return;
      const lockDate = fixture.lockTimestamp.toDate
        ? fixture.lockTimestamp.toDate()
        : new Date(fixture.lockTimestamp);
      const diff = lockDate - Date.now();
      if (diff <= 0) {
        if (mounted) setTimeLeft(t('match_closed'));
        return;
      }
      const totalSecs = Math.floor(diff / 1000);
      const days = Math.floor(totalSecs / 86400);
      const hours = Math.floor((totalSecs % 86400) / 3600);
      const mins = Math.floor((totalSecs % 3600) / 60);
      const secs = totalSecs % 60;
      if (mounted) setUrgent(diff < 3600000);
      if (days > 0) { if (mounted) setTimeLeft(`${days}d ${hours}h ${mins}m`); }
      else if (hours > 0) { if (mounted) setTimeLeft(`${hours}h ${mins}m ${secs}s`); }
      else { if (mounted) setTimeLeft(`${mins}m ${secs}s`); }
    }
    update();
    const interval = setInterval(update, 1000);
    return () => { mounted = false; clearInterval(interval); };
  }, [fixture.lockTimestamp]);

  if (!timeLeft || timeLeft === t('match_closed') || timeLeft === t('match_closed')) return null;

  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontSize: 11, fontFamily: "'Oswald',sans-serif", letterSpacing: 1,
      color: urgent ? '#E63946' : '#F0B429',
      background: urgent ? 'rgba(230,57,70,0.1)' : 'rgba(240,180,41,0.08)',
      border: `1px solid ${urgent ? 'rgba(230,57,70,0.25)' : 'rgba(240,180,41,0.2)'}`,
      borderRadius: 20, padding: '3px 10px',
      animation: urgent ? 'pulse 1s ease-in-out infinite' : 'none'
    }}>
      ⏱ {timeLeft}
    </div>
  );
}
// ── ANUNCIO INTERSTICIAL RELAJADO ────────────────────────
async function showRelaxedInterstitial(groupPlan) {
  // 1. Si no es la app nativa en el celular, no hace nada
  if (!window.Capacitor?.isNative) return;

  // 2. Si el grupo es de pago, NO mostrar anuncios
  if (groupPlan && groupPlan !== 'free') return;
  
  // 100% de probabilidad — solo 2-3 predicciones al día en el Mundial
  // (sin filtro aleatorio)

  try {
    const { AdMob } = await import('@capacitor-community/admob');
    await AdMob.prepareInterstitial({
      adId: ADMOB_INTERSTITIAL_ID,
      isTesting: process.env.NODE_ENV === 'development',
    });
    await AdMob.showInterstitial();
  } catch (err) {
    console.log('Error mostrando anuncio de pantalla completa:', err);
  }
}
function MatchCard({ fixture, prediction, allPredictions = [], members = [], onSave, index, groupPlan, myUserId }) {
  const { t } = useTranslation();
  const locked = isMatchLocked(fixture.lockTimestamp);
  const [editing, setEditing] = useState(false);
  const [home, setHome] = useState(prediction?.homeScore ?? '');
  const [away, setAway] = useState(prediction?.awayScore ?? '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  async function handleSave() {
    if (home === '' || away === '') return;
    setSaving(true);
    const ok = await onSave(fixture.id, home, away);
    setSaving(false);
    if (ok) {
      setSaved(true);
      SoundManager.play('save');
      setEditing(false);
      setTimeout(() => setSaved(false), 2000);
      
      // Anuncio nativo (AdMob) — solo app, 30% de probabilidad
      showRelaxedInterstitial(groupPlan);
      // Anuncio web (Adsterra popunder) — solo web, solo grupos gratis, 1 vez por sesión
      showAdsterraPopunder(groupPlan);
    }
  }

  const hasPred = prediction !== undefined;
  const isTBD = fixture.homeTeam === 'TBD' || fixture.awayTeam === 'TBD';

  return (
    <div style={{ 
      background: C.card, 
      border: `1px solid ${saved ? 'rgba(6, 184, 106, 0.4)' : C.border}`, 
      borderRadius: 20, 
      padding: '18px', 
      marginBottom: 4,
      boxShadow: saved ? '0 4px 20px rgba(6, 184, 106, 0.15)' : '0 4px 20px rgba(0,0,0,0.2)',
      transition: 'all 0.3s ease',
      animation: `fadeIn 0.4s ease ${index * 0.05}s both`
    }}
    className="card-hover"
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ 
          fontFamily: "'Inter',sans-serif", 
          fontSize: 10, 
          color: '#6B6B7B', 
          letterSpacing: 1,
          textTransform: 'uppercase',
          fontWeight: 500
        }}>
          {fixture.phase?.toUpperCase()} {fixture.group ? `· GRUPO ${fixture.group}` : ''}
        </div>
        <div style={{ 
          fontSize: 10, 
          fontFamily: "'Oswald',sans-serif", 
          letterSpacing: 1, 
          color: locked ? C.red : C.green, 
          background: locked ? 'rgba(230,57,70,0.1)' : 'rgba(6,184,106,0.1)', 
          padding: '5px 10px', 
          borderRadius: 20,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 4
        }}>
          {locked ? '🔒' : '✓'} {locked ? t('match_closed') : t('match_open')}
        </div>
      </div>

      {/* Teams */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {/* Local */}
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ marginBottom: 10 }}>
            <FlagImg code={fixture.homeTeam} size={48} />
          </div>
          <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 13, letterSpacing: 0.5, color: '#ccc', fontWeight: 500 }}>{fixture.homeTeamName}</div>
        </div>

        {/* Score */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          {editing && !locked ? (
            <>
              <ScoreInput value={home} onChange={setHome} />
              <span style={{ fontFamily: "'Oswald',sans-serif", fontSize: 20, color: '#444', fontWeight: 700 }}>:</span>
              <ScoreInput value={away} onChange={setAway} />
            </>
          ) : (
            <div style={{ 
              fontFamily: "'Oswald',sans-serif", 
              fontSize: 36, 
              letterSpacing: 2, 
              color: hasPred ? C.gold : '#333', 
              minWidth: 80, 
              textAlign: 'center',
              fontWeight: 700,
              textShadow: hasPred ? '0 0 20px rgba(240, 180, 41, 0.3)' : 'none'
            }}>
              {hasPred ? `${prediction.homeScore}:${prediction.awayScore}` : '?-?'}
            </div>
          )}
        </div>

        {/* Visitante */}
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ marginBottom: 10 }}>
            <FlagImg code={fixture.awayTeam} size={48} />
          </div>
          <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 13, letterSpacing: 0.5, color: '#ccc', fontWeight: 500 }}>{fixture.awayTeamName}</div>
        </div>
      </div>

      {/* Date */}
      <div style={{ 
        textAlign: 'center', 
        fontFamily: "'Inter',sans-serif", 
        fontSize: 12, 
        color: '#6B6B7B', 
        marginTop: 14,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6
      }}>
        <span>📅</span> {formatDate(fixture)} · {fixture.stadium}
      </div>

      {/* Temporizador */}
      {!locked && (
        <div style={{ textAlign: 'center', marginTop: 8, marginBottom: 2 }}>
          <Countdown fixture={fixture} />
        </div>
      )}

      {/* Actions */}
      {!locked && !isTBD && (
        <div style={{ marginTop: 18, display: 'flex', gap: 10, justifyContent: 'center' }}>
          {editing ? (
            <>
              <button onClick={handleSave} disabled={saving || home === '' || away === ''}
                style={{ 
                  padding: '12px 24px', 
                  background: `linear-gradient(135deg, ${C.gold} 0%, ${C.goldDark} 100%)`, 
                  border: 'none', 
                  borderRadius: 12, 
                  fontFamily: "'Oswald',sans-serif", 
                  fontSize: 13, 
                  letterSpacing: 1, 
                  color: C.bg, 
                  cursor: 'pointer', 
                  opacity: (saving || home === '' || away === '') ? 0.5 : 1,
                  fontWeight: 600,
                  boxShadow: '0 4px 15px rgba(240, 180, 41, 0.3)',
                  transition: 'all 0.3s ease'
                }}
                className="btn-gold"
              >
                {saving ? '...' : '💾 GUARDAR'}
              </button>
              <button onClick={() => { setEditing(false); setHome(prediction?.homeScore ?? ''); setAway(prediction?.awayScore ?? ''); }}
                style={{ 
                  padding: '12px 18px', 
                  background: 'rgba(255,255,255,0.05)', 
                  border: `1px solid ${C.border}`, 
                  borderRadius: 12, 
                  fontFamily: "'Oswald',sans-serif", 
                  fontSize: 13, 
                  color: '#6B6B7B', 
                  cursor: 'pointer',
                  fontWeight: 500,
                  transition: 'all 0.3s ease'
                }}
              >
                ✕ CANCELAR
              </button>
            </>
          ) : (
            <button onClick={() => setEditing(true)}
              style={{ 
                padding: '10px 22px', 
                background: hasPred ? 'rgba(240,180,41,0.1)' : 'rgba(255,255,255,0.05)', 
                border: `1px solid ${hasPred ? C.borderGold : C.border}`, 
                borderRadius: 12, 
                fontFamily: "'Oswald',sans-serif", 
                fontSize: 12, 
                letterSpacing: 1, 
                color: hasPred ? C.gold : '#6B6B7B', 
                cursor: 'pointer',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                transition: 'all 0.3s ease'
              }}
              className="card-hover"
            >
              {hasPred ? '✏️' : '+'} {hasPred ? t('btn_edit') : t('btn_predict')}
            </button>
          )}
        </div>
      )}

      {saved && (
        <div style={{ 
          textAlign: 'center', 
          fontFamily: "'Inter',sans-serif", 
          fontSize: 13, 
          color: C.green, 
          marginTop: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          animation: 'fadeIn 0.3s ease'
        }}>
          <span>✓</span> Predicción guardada
        </div>
      )}

      {/* PREDICCIONES DE TODOS — visible cuando el partido está cerrado */}
      {locked && allPredictions.length > 0 && (
        <MatchPredictions
          allPredictions={allPredictions}
          members={members}
          myUserId={myUserId}
          fixture={fixture}
        />
      )}
    </div>
  );
}

// ── PREDICCIONES DE TODOS EN UN PARTIDO ─────────────────
function MatchPredictions({ allPredictions, members, myUserId, fixture }) {
  const [open, setOpen] = useState(false);
  const total = allPredictions.length;

  // Ordenar: primero el usuario, luego por puntos desc
  const sorted = [...allPredictions].sort((a, b) => {
    if (a.userId === myUserId) return -1;
    if (b.userId === myUserId) return 1;
    return (b.points || 0) - (a.points || 0);
  });

  return (
    <div style={{ borderTop: `1px solid rgba(255,255,255,0.06)` }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', background: 'none', border: 'none', cursor: 'pointer',
          padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>🔒</span>
          <span style={{ fontFamily: "'Inter',sans-serif", fontSize: 11, color: '#6B6B7B' }}>
            Ver predicciones del grupo ({total})
          </span>
        </div>
        <span style={{ color: '#6B6B7B', fontSize: 11, display: 'inline-block', transition: 'transform 0.3s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
      </button>

      {open && (
        <div style={{ padding: '0 16px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {sorted.map(p => {
            const member = members.find(m => m.userId === p.userId || m.id === p.userId);
            const name = member?.name || 'Participante';
            const pts = p.points || 0;
            const isMe = p.userId === myUserId;
            const isExact = p.breakdown?.some(b => b.key === 'exactScore');
            return (
              <div key={p.id || p.userId} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '8px 12px', borderRadius: 10,
                background: isMe ? 'rgba(240,180,41,0.08)' : isExact ? 'rgba(6,184,106,0.06)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${isMe ? 'rgba(240,180,41,0.3)' : isExact ? 'rgba(6,184,106,0.15)' : 'rgba(255,255,255,0.05)'}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: isMe ? 'rgba(240,180,41,0.2)' : 'rgba(255,255,255,0.06)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: "'Oswald',sans-serif", fontSize: 12,
                    color: isMe ? '#F0B429' : '#6B6B7B', fontWeight: 700,
                  }}>
                    {name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 12, color: isMe ? '#F0B429' : '#ccc' }}>
                      {name} {isMe && <span style={{ fontSize: 9, color: '#F0B429', background: 'rgba(240,180,41,0.1)', padding: '1px 6px', borderRadius: 8, marginLeft: 4 }}>TÚ</span>}
                    </div>
                    {p.calculated && (
                      <div style={{ fontSize: 10, color: pts > 0 ? '#06B86A' : '#555', fontFamily: "'Oswald',sans-serif" }}>
                        {pts > 0 ? `+${pts} pts ${isExact ? '🎯' : ''}` : '0 pts'}
                      </div>
                    )}
                    {!p.calculated && (
                      <div style={{ fontSize: 10, color: '#444', fontFamily: "'Inter',sans-serif" }}>⏳ Pendiente</div>
                    )}
                  </div>
                </div>
                <div style={{
                  fontFamily: "'Oswald',sans-serif", fontSize: 18, fontWeight: 700,
                  color: isMe ? '#F0B429' : isExact ? '#06B86A' : '#888', letterSpacing: 2,
                }}>
                  {p.homeScore}-{p.awayScore}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── SCORE INPUT MEJORADO ─────────────────────────────────
function ScoreInput({ value, onChange }) {
  const { t } = useTranslation();
  return (
    <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <button onClick={() => onChange(v => Math.max(0, parseInt(v || 0) - 1))}
        style={{ 
          background: 'none', 
          border: 'none', 
          color: '#6B6B7B', 
          cursor: 'pointer', 
          fontSize: 16,
          padding: '2px 10px',
          transition: 'color 0.2s'
        }}
        onMouseEnter={e => e.target.style.color = '#fff'}
        onMouseLeave={e => e.target.style.color = '#6B6B7B'}
      >▲</button>
      
      <input 
        type="number" 
        min={0} 
        max={20} 
        value={value}
        onChange={e => onChange(Math.max(0, Math.min(20, parseInt(e.target.value) || 0)))}
        style={{ 
          width: 50, 
          height: 50, 
          textAlign: 'center', 
          background: 'rgba(240,180,41,0.05)', 
          border: `2px solid ${C.borderGold}`, 
          borderRadius: 12, 
          color: C.gold, 
          fontFamily: "'Oswald',sans-serif", 
          fontSize: 26, 
          fontWeight: 700,
          outline: 'none',
          transition: 'all 0.3s ease'
        }}
        onFocus={e => {
          e.target.style.boxShadow = '0 0 20px rgba(240, 180, 41, 0.2)';
          e.target.style.background = 'rgba(240,180,41,0.1)';
        }}
        onBlur={e => {
          e.target.style.boxShadow = 'none';
          e.target.style.background = 'rgba(240,180,41,0.05)';
        }}
      />
      
      <button onClick={() => onChange(v => Math.min(20, parseInt(v || 0) + 1))}
        style={{ 
          background: 'none', 
          border: 'none', 
          color: '#6B6B7B', 
          cursor: 'pointer', 
          fontSize: 16,
          padding: '2px 10px',
          transition: 'color 0.2s'
        }}
        onMouseEnter={e => e.target.style.color = '#fff'}
        onMouseLeave={e => e.target.style.color = '#6B6B7B'}
      >▼</button>
    </div>
  );
}

// ── HELPERS ───────────────────────────────────────────────
function formatDate(fixture) {
  // Usa kickoffTimestamp (UTC) para mostrar hora local del usuario
  if (fixture.kickoffTimestamp) {
    return formatMatchLocal(fixture.kickoffTimestamp);
  }
  // Fallback si no hay timestamp
  if (!fixture.date) return '';
  const months = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
  const [y, m, d] = fixture.date.split('-');
  return `${parseInt(d)} ${months[parseInt(m) - 1]} · ${fixture.time || ''}`;
}

function EmptyState({ icon, msg }) {
  return (
    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
      <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }}>{icon}</div>
      <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 14, color: '#6B6B7B' }}>{msg}</div>
    </div>
  );
}

function LoadingScreen() {
  const { t } = useTranslation();
  return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ 
          width: 50, 
          height: 50, 
          border: '4px solid rgba(255,255,255,0.1)', 
          borderTop: `4px solid ${C.gold}`, 
          borderRadius: '50%', 
          margin: '0 auto 20px',
          animation: 'spin 1s linear infinite'
        }} />
        <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 18, letterSpacing: 2, color: C.gold, fontWeight: 600 }}>{t('lbl_loading')}</div>
      </div>
    </div>
  );
}